/**
 * @file LabelPrintLog.tsx
 * @description Журнал печати этикеток документа: когда, кто, какие строки,
 *   сколько, шаблоны, файл и «Перепечатать» (phase19.22, W17-5a, W17-9c;
 *   ui-design §1).
 *
 * Две подачи одних данных: карточки (`<lg` и шторка) и таблица (`≥lg`,
 * секция под «Движениями»). Файл устаревшей задачи скрыт (W17-9c) — запись
 * остаётся, перепечатка доступна всегда: она берёт ТЕКУЩИЕ данные партий.
 *
 * Журнал этикеток ячеек (phase19.22 шаг 5) — те же карточки: вместо «Строк»
 * документа — сводка адресов (`binSummary`: ячейки, полки, стеллажи).
 *
 * «Скрыть файл» вручную (шаг 5, решение 2026-09-26) — пункт «⋯» записи с
 * подтверждением; запись со скрытым файлом приглушена, «Открыть» у неё нет.
 */

import { useState } from "react";

import { LabelJobActions } from "./LabelJobActions";
import { LabelJobHideConfirm } from "./LabelJobHideConfirm";
import { formatDocDateTime } from "../wms-document-meta";
import { LIST_FRAME_CLASS, LIST_SURFACE_CLASS } from "../../ui/ListStates";
import { LABEL_TEMPLATE_META, type LabelPrintJobDto } from "../../../lib/api-labels";
import { cn } from "@/lib/utils";

interface Props {
  jobs: LabelPrintJobDto[];
  reprintingId?: string | null;
  /** Не передан — кнопки «Перепечатать» нет. */
  onReprint?: (job: LabelPrintJobDto) => void;
  /** Не передан — пункта «Скрыть файл» нет; `true` — файл скрыт. */
  onHide?: (job: LabelPrintJobDto) => Promise<boolean>;
  hidingId?: string | null;
  /** false — без строк документа (у печати ячеек их нет). */
  showLines?: boolean;
  emptyText?: string;
  /** `cards` — только карточки (шторка), `auto` — карточки на `<lg`, таблица на `≥lg`. */
  layout?: "cards" | "auto";
}

/** «ячеек 12 · полок 3 · стеллаж A» — у задач печати адресов. */
function binSummaryText(job: LabelPrintJobDto): string {
  const s = job.binSummary;
  if (!s) return "";
  const parts = [`ячеек ${s.binsCount}`];
  if (s.shelvesCount > 0) parts.push(`полок ${s.shelvesCount}`);
  if (s.racks.length > 0) parts.push(`стеллаж ${s.racks.join(", ")}`);
  return `${parts.join(" · ")} · `;
}

function templatesText(job: LabelPrintJobDto): string {
  return job.templates.map((t) => LABEL_TEMPLATE_META[t].short).join(", ");
}

/** Файл скрыт — запись приглушена (кнопки при этом не гасим: перепечатка работает). */
const isHidden = (job: LabelPrintJobDto) => job.file.state === "hidden";

export function LabelPrintLog({
  jobs,
  reprintingId = null,
  onReprint,
  onHide,
  hidingId = null,
  showLines = true,
  emptyText = "Этикетки по документу ещё не печатались.",
  layout = "auto",
}: Props) {
  const [confirmJob, setConfirmJob] = useState<LabelPrintJobDto | null>(null);
  if (jobs.length === 0) {
    return <p className="text-sm text-on-surface-variant">{emptyText}</p>;
  }
  const cardsClass = layout === "cards" ? "flex flex-col gap-2" : "flex flex-col gap-2 lg:hidden";
  const onHideRequest = onHide ? setConfirmJob : undefined;
  const actions = (job: LabelPrintJobDto) => (
    <LabelJobActions
      job={job}
      reprinting={reprintingId === job.id}
      onReprint={onReprint}
      onHideRequest={onHideRequest}
    />
  );

  return (
    <>
      <ul className={cardsClass}>
        {jobs.map((job) => (
          <li
            key={job.id}
            data-hidden={isHidden(job) || undefined}
            className={cn(
              "flex flex-col gap-2 rounded-xl p-3 text-sm ring-ghost",
              LIST_SURFACE_CLASS,
              isHidden(job) && "bg-surface-low text-on-surface-variant",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium tabular-nums">{formatDocDateTime(job.createdAt)}</span>
              <span className="text-on-surface-variant">{job.createdBy.displayName}</span>
            </div>
            <span className="text-on-surface-variant">
              {showLines ? `Строки ${job.receiptLineNos.join(", ") || "—"} · ` : binSummaryText(job)}
              {job.labelsCount} эт. · {templatesText(job)}
            </span>
            {job.error ? <span className="text-xs text-destructive">{job.error}</span> : null}
            {actions(job)}
          </li>
        ))}
      </ul>

      {layout === "auto" ? (
        // SC69: таблица журнала — в рамке на непрозрачной поверхности.
        <div className={cn("hidden lg:block", LIST_FRAME_CLASS)}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-on-surface-variant">
                <th className="px-3 py-2 font-medium">Когда</th>
                <th className="px-3 py-2 font-medium">Кто</th>
                <th className="px-3 py-2 font-medium">Строки</th>
                <th className="px-3 py-2 text-right font-medium">Эт.</th>
                <th className="px-3 py-2 font-medium">Шаблоны</th>
                <th className="px-3 py-2 font-medium">Файл</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  className={cn(
                    "border-t border-outline-variant align-middle",
                    isHidden(job) && "bg-surface-low text-on-surface-variant",
                  )}
                >
                  <td className="px-3 py-2 tabular-nums">{formatDocDateTime(job.createdAt)}</td>
                  <td className="px-3 py-2">{job.createdBy.displayName}</td>
                  <td className="px-3 py-2 tabular-nums">{job.receiptLineNos.join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{job.labelsCount}</td>
                  <td className="px-3 py-2">{templatesText(job)}</td>
                  <td className="px-3 py-2">{actions(job)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {onHide ? (
        <LabelJobHideConfirm
          open={confirmJob !== null}
          busy={confirmJob !== null && hidingId === confirmJob.id}
          onCancel={() => setConfirmJob(null)}
          onConfirm={() => {
            if (!confirmJob) return;
            void onHide(confirmJob).then((ok) => {
              if (ok) setConfirmJob(null);
            });
          }}
        />
      ) : null}
    </>
  );
}
